/**
 * Electron preload 가 노출하는 네이티브 API (브릿지 PC 안에서 콘솔을 열었을 때만 있다).
 * 기획 문서 "브릿지 화면은 웹, 네이티브는 Electron" 의 함수 목록과 같다.
 * 토큰은 넘어오지 않는다. 콘솔은 getUiSession 으로 받은 15분 JWT 만 쓴다.
 */
export type NativeResult = { ok: boolean; message?: string };
export type LocalStatus = {
  socket: "connected" | "connecting" | "disconnected";
  lastReceivedAt: string | null;
  engineError: string | null;
  codex: { loggedIn: boolean; detail?: string; installed?: boolean; version?: string; throttledUntil?: string | null };
  devices: { serial: string; model?: string; state: string; batteryPct?: number; charging?: boolean; screenOn?: boolean; locked?: boolean; keyboardReady?: boolean }[];
  /** 0.3.2+ (features: localStatusV2). 없으면 화면에서 "확인 불가"/생략. */
  engine?: { running: boolean; startedAt?: string; uptimeSec?: number; respondingAt?: string };
  outboxPending?: number;
  /** 마지막 연결 장애 (0.3.3+). 원문 오류는 없다. */
  lastOutage?: { disconnectedAt: string; reconnectedAt: string; durationSec: number; reason: "server_unreachable" | "network" | "app_restart" | "token_rejected" | "unknown"; attempts: number };
  reconnects24h?: number;
  appVersion?: string;
  /** 원격 업데이트 상태 (0.3.6+: idle·pending·downloading·installing·failed). 이전 값은 호환용. */
  update?: { state: "idle" | "pending" | "downloading" | "installing" | "failed" | "none" | "checking" | "available" | "ready" | "unsupported"; version?: string; progressPct?: number; message?: string };
  /** 자동 시작·감시 작업 설정 (0.3.6+). */
  watchdog?: boolean;
  runningTasks?: number;
};
export type NativeIdentity = {
  machineId: string;
  customerId: string | null;
  label: string | null;
  engineVersion: string;
  nativeApiVersion: number;
  serverUrl: string;
  appVersion?: string;
  features?: string[];
};
export type UiSession = { accessToken: string; expiresIn: number } | { error: "revoked" | "offline" | "unpaired" };

export interface MetaCrewNative {
  version: number;
  getIdentity(): Promise<NativeIdentity | null>;
  getUiSession(): Promise<UiSession>;
  pair(code: string): Promise<NativeResult>;
  unpair(): Promise<NativeResult>;
  codexLogin(): Promise<NativeResult>;
  codexLogout(): Promise<NativeResult>;
  setupPhone(serial?: string): Promise<NativeResult>;
  getLocalStatus(): Promise<LocalStatus>;
  onLocalStatus(cb: (s: LocalStatus) => void): () => void;
  openLogs(): Promise<NativeResult>;
  restart(): Promise<NativeResult>;
  checkUpdate(): Promise<NativeResult>;
  /** 0.3.2+: OS 알림 (창이 포커스가 아닐 때). 없으면 앱 안 알림만. */
  notify?(title: string, body: string): Promise<NativeResult>;
}

declare global {
  interface Window {
    metaCrewNative?: MetaCrewNative;
  }
}

export const native = (): MetaCrewNative | undefined => (typeof window !== "undefined" ? window.metaCrewNative : undefined);
export const inBridge = (): boolean => !!native();
